/**
 * @unscoped — Layout (NavBar / TopBar) 의 i18n — Cycle 70.
 *
 * cycle 67-69 의 page-level false negative cleanup 완결 후 component-level 정찰 — 13 components
 * 가 hardcoded korean (총 71). NavBar (12) + TopBar (13) = 25 hardcoded 가 가장 사용자 가시
 * (모든 page 상단 + 좌측 메뉴 항상 표시).
 *
 * cycle 51 calendar / cycle 54 mail / cycle 55 onboarding / cycle 57 board / cycle 58 task /
 * cycle 59-60 settings / cycle 61 chat / cycle 68 payroll / cycle 69 sysadmin / cycle 70 layout
 * = 9 신규 모듈.
 *
 * 공통 route labels SSOT — NavBar.PRIMARY_NAV.label 과 TopBar.ROUTE_LABELS 가 동일 텍스트라
 * `layout.nav.*` 키 공유 (drift 차단).
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 *   - params 치환 ({name}) 지원
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ===== NavBar (PRIMARY_NAV + SECONDARY_NAV) + TopBar ROUTE_LABELS 공유 =====
  'layout.nav.dashboard': { ko: '대시보드', en: 'Dashboard', ja: 'ダッシュボード' },
  'layout.nav.chat': { ko: '채팅', en: 'Chat', ja: 'チャット' },
  'layout.nav.mail': { ko: '메일', en: 'Mail', ja: 'メール' },
  'layout.nav.contacts': { ko: '명함첩', en: 'Contacts', ja: 'アドレス帳' },
  // AI (설계 판 A, 2026-09-09) — 레일 메뉴 이름. 화면 제목·씨앗 라벨과 같은 낱말을 쓴다.
  'layout.nav.ai': { ko: 'AI', en: 'AI', ja: 'AI' },
  'layout.nav.calendar': { ko: '캘린더', en: 'Calendar', ja: 'カレンダー' },
  'layout.nav.task': { ko: '업무관리', en: 'Tasks', ja: 'タスク管理' },
  // project-menu-detailing (2026-06-10) — 프로젝트 대시보드 메뉴.
  'layout.nav.project': { ko: '프로젝트', en: 'Projects', ja: 'プロジェクト' },
  'layout.nav.workspace': { ko: '워크스페이스', en: 'Workspace', ja: 'ワークスペース' },
  'layout.nav.info': { ko: '알림사항', en: 'Notices', ja: 'お知らせ' },
  // 다중 게시판 (migration 102, 2026-08-18) — 알림사항 자리를 게시판이 잇는다.
  'layout.nav.board': { ko: '게시판', en: 'Boards', ja: '掲示板' },
  'layout.nav.organization': { ko: '조직도', en: 'Organization', ja: '組織' },
  // 2026-08-23 — 근태(근태 1단계). 레지스트리 label 이 SSOT 지만, fallback 목록과 전체메뉴가 이 키를 쓴다.
  'layout.nav.attendance': { ko: '근태', en: 'Attendance', ja: '勤怠' },
  'layout.nav.hr': { ko: '인사', en: 'HR', ja: '人事' },
  'layout.nav.approval': { ko: '전자결재', en: 'Approval', ja: '電子決裁' },
  'layout.nav.payroll': { ko: '급여', en: 'Payroll', ja: '給与' },
  // 2026-09-16 — 업무보고. 레지스트리에는 처음부터 있었는데(씨앗 menu_key='briefing') 코드 폴백에만
  //   없어서, /menu-config 조회가 실패하는 경로에서 관리자·대표에게도 안 보였다. 그 폴백을 채우며
  //   라벨 키도 함께 만든다(ko 는 씨앗 label 과 같은 '업무 보고' 로 맞춘다 — 두 경로가 같은 글자를 보이게).
  'layout.nav.briefing': { ko: '업무 보고', en: 'Briefing', ja: '業務報告' },
  'layout.nav.settings': { ko: '설정', en: 'Settings', ja: '設定' },
  // 설정 → 내 프로필 진입점 전환 (design-20260606 §17). settings 키는 호환 위해 보존.
  'layout.nav.profile': { ko: '내 프로필', en: 'My Profile', ja: 'マイプロフィール' },
  // 2026-07-24 — 좌측 바 하단 '통합설정'(/settings). 기존 layout.nav.settings('설정')은 무수정 보존.
  'layout.nav.settings_integrated': { ko: '통합설정', en: 'Settings', ja: '統合設定' },
  // 2026-09-13 — 도움말(D). 레일 하단(조직도 · 도움말 · 통합설정)과 도움말 홈.
  'layout.nav.help': { ko: '도움말', en: 'Help', ja: 'ヘルプ' },
  'layout.help.description': {
    ko: '화면을 쓰다가 막히면 여기서 찾습니다.',
    en: 'Look here when you get stuck on a screen.',
    ja: '画面の使い方に迷ったらここで探します。',
  },
  'layout.help.list_aria': { ko: '도움말 목록', en: 'Help topics', ja: 'ヘルプ一覧' },
  'layout.help.empty_title': { ko: '아직 준비된 도움말이 없습니다', en: 'No help topics yet', ja: 'まだヘルプがありません' },
  'layout.help.empty_desc': {
    ko: '화면마다 제목 옆 도움말 단추에서 그 화면의 설명을 볼 수 있습니다.',
    en: 'Each screen has a help button next to its title.',
    ja: '各画面のタイトル横のヘルプボタンから説明を見られます。',
  },
  'layout.help.bulk_import_title': { ko: '사용자 대량 등록 매뉴얼', en: 'Bulk user import manual', ja: 'ユーザー一括登録マニュアル' },
  'layout.help.bulk_import_sub': {
    ko: 'CSV 로 사용자를 한 번에 올릴 때 — 오류 코드와 고치는 법 · 관리자',
    en: 'Uploading users by CSV — error codes and fixes · Admin',
    ja: 'CSV で一括登録するとき — エラーコードと対処 · 管理者',
  },

  // ===== 세션 유지 타이머 (좌측 사이드바 하단) =====
  /**
   * 이탈 경고 (2026-09-08 사용자 지시) — **등록 · 수정이 있는 화면 전부가 같은 말을 쓴다.**
   * 화면마다 문장을 새로 지으면 같은 위험이 자리마다 다른 무게로 읽힌다.
   */
  'layout.unsaved.title': { ko: '나가면 쓰던 것이 사라집니다', en: 'Leave without saving?', ja: '保存せずに移動しますか' },
  'layout.unsaved.desc': {
    ko: '저장하지 않은 내용이 있습니다. 이 화면을 떠나면 되돌릴 수 없습니다.',
    en: 'You have unsaved changes. Leaving this screen cannot be undone.',
    ja: '保存していない内容があります。この画面を離れると元に戻せません。',
  },
  'layout.unsaved.stay': { ko: '계속 작성', en: 'Keep editing', ja: '編集を続ける' },
  'layout.unsaved.leave': { ko: '나가기', en: 'Leave', ja: '移動する' },
  'layout.session.label': { ko: '세션 유지', en: 'Session', ja: 'セッション' },
  'layout.session.expired': { ko: '만료', en: 'Expired', ja: '期限切れ' },
  'layout.session.title': {
    ko: '세션 잔여 {time} — 활동 시 자동 연장',
    en: 'Session {time} left — extends on activity',
    ja: 'セッション残り {time} — 操作で自動延長',
  },
  // 화면잠금(2026-08-19)이 켜져 있으면 0 이 되는 순간 로그아웃이 아니라 **잠금**이다.
  'layout.session.title_lock': {
    ko: '화면잠금까지 {time} — 활동 시 자동 연장',
    en: 'Screen locks in {time} — extends on activity',
    ja: '画面ロックまで {time} — 操作で自動延長',
  },

  // ===== NavBar 접기/펼치기 토글 (SidebarToggle) =====
  'layout.nav.collapse': { ko: '사이드바 접기', en: 'Collapse sidebar', ja: 'サイドバーを折りたたむ' },
  'layout.nav.expand': { ko: '사이드바 펼치기', en: 'Expand sidebar', ja: 'サイドバーを展開' },

  // ===== TopBar 전용 =====
  'layout.topbar.home': { ko: '홈', en: 'Home', ja: 'ホーム' },
  'layout.topbar.search_placeholder': { ko: '전역 검색…', en: 'Global search…', ja: 'グローバル検索...' },
  'layout.topbar.search_aria': { ko: '전역 검색', en: 'Global search', ja: 'グローバル検索' },
  'layout.topbar.aria_bell_default': { ko: '알림', en: 'Notifications', ja: '通知' },
  'layout.topbar.aria_bell_count': {
    ko: '알림 {n}건',
    en: 'Notifications: {n}',
    ja: '通知 {n}件',
  },
  'layout.topbar.aria_theme_color': { ko: '테마 색상', en: 'Theme color', ja: 'テーマカラー' },
  'layout.topbar.aria_toggle_mode': { ko: '모드 전환', en: 'Toggle mode', ja: 'モード切替' },

  // ===== v2.12 — TopBar 시안 B (타이틀 + 섹션 설명) =====
  // 타이틀은 위 layout.nav.* 재사용. 아래는 화면별 한 줄 설명 + 신규 타이틀 2종.
  'layout.nav.ai_minutes': { ko: 'AI 회의록', en: 'AI Minutes', ja: 'AI議事録' },
  // 2026-08-12 — 예약 메뉴(레지스트리 label='예약관리'이나 사용자 레일 표기는 '예약').
  'layout.nav.reservation': { ko: '예약', en: 'Reservations', ja: '予約' },
  'layout.nav.system_admin': { ko: '시스템 관리', en: 'System Admin', ja: 'システム管理' },
  'layout.topbar.desc.dashboard': { ko: '오늘 일정·메일·태스크를 한눈에 확인하세요', en: 'Your events, mail, and tasks at a glance', ja: '本日の予定・メール・タスクを一目で' },
  'layout.topbar.desc.chat': { ko: '팀·동료와 실시간으로 소통하세요', en: 'Chat with your team in real time', ja: 'チーム・同僚とリアルタイムで対話' },
  'layout.topbar.desc.mail': { ko: '받은 메일과 보낸 메일을 관리합니다', en: 'Manage your inbox and sent mail', ja: '受信・送信メールを管理' },
  'layout.topbar.desc.calendar': { ko: '일정·할일·기념일을 한곳에서 관리합니다', en: 'Manage events, to-dos, and anniversaries', ja: '予定・タスク・記念日を一元管理' },
  'layout.topbar.desc.task': { ko: '팀·프로젝트의 태스크를 관리합니다', en: 'Manage your team and project tasks', ja: 'チーム・プロジェクトのタスクを管理' },
  'layout.topbar.desc.project': { ko: '팀·프로젝트 현황과 통계를 확인하세요', en: 'Track project status and statistics', ja: 'プロジェクトの状況と統計を確認' },
  'layout.topbar.desc.workspace': { ko: '파일과 문서를 공유하고 관리합니다', en: 'Share and manage files and documents', ja: 'ファイルと文書を共有・管理' },
  'layout.topbar.desc.info': { ko: '공지와 안내 사항을 확인하세요', en: 'Check notices and announcements', ja: 'お知らせと案内を確認' },
  'layout.topbar.desc.board': { ko: '회사·부서·팀 게시판을 확인하세요', en: 'Company, division and team boards', ja: '会社・部署・チームの掲示板' },
  'layout.topbar.desc.ai_minutes': { ko: '회의 원문을 AI가 구조화해 드립니다', en: 'AI structures your meeting transcripts', ja: '会議原文をAIが構造化' },
  'layout.topbar.desc.me': { ko: '계정과 환경을 관리합니다', en: 'Manage your account and preferences', ja: 'アカウントと環境を管理' },
  'layout.topbar.desc.settings': { ko: '환경과 테마를 설정합니다', en: 'Configure preferences and theme', ja: '環境とテーマを設定' },
  'layout.topbar.desc.system_admin': { ko: '테넌트와 운영을 관리합니다', en: 'Manage tenants and operations', ja: 'テナントと運用を管理' },

  // ===== 2026-08-11 — 좌측 메뉴 세로 배치 + 최하단 전체메뉴 (AllMenuPanel) =====
  'layout.nav.all_menu': { ko: '전체메뉴', en: 'All menus', ja: '全メニュー' },
  'layout.allmenu.group_work': { ko: '업무', en: 'Work', ja: '業務' },
  'layout.allmenu.group_org': { ko: '조직 · 설정', en: 'Organization & settings', ja: '組織・設定' },
  'layout.allmenu.close': { ko: '전체메뉴 닫기', en: 'Close all menus', ja: '全メニューを閉じる' },
  'layout.allmenu.count': { ko: '{n}개', en: '{n} menus', ja: '{n}件' },

  // ===== 2026-08-19 — 보조영역(관리 콘솔 위치='aux') + 전체메뉴 별(★) 고정 =====
  // 보조영역 메뉴는 좌측 레일에 걸리지 않고 전체메뉴에서만 보인다 → 별도 그룹으로 구분해 준다.
  'layout.allmenu.group_aux': { ko: '보조', en: 'More', ja: 'その他' },
  'layout.allmenu.pin_add': { ko: '{name} 우측 레일에 고정', en: 'Pin {name} to right rail', ja: '{name} を右レールに固定' },
  'layout.allmenu.pin_remove': { ko: '{name} 우측 레일에서 해제', en: 'Unpin {name} from right rail', ja: '{name} を右レールから解除' },
  // ===== 2026-08-26 — 좌측 레일 넘침 접기(«더보기») =====
  // 레일에 안 들어간 메뉴를 담는 칸. 전체메뉴(모두 보여 준다)와 역할이 다르다 — 이쪽은
  // **이 레일에서 접힌 것만** 보여 준다. 라벨은 레일 글자칸(72px)에 들어가는 3자로.
  'layout.nav.rail_more': { ko: '더보기', en: 'More', ja: 'その他' },
  'layout.railmore.title': { ko: '레일에 안 들어간 메뉴', en: "Menus that didn't fit", ja: 'レールに入らないメニュー' },
  'layout.railmore.close': { ko: '더보기 닫기', en: 'Close more menus', ja: 'その他を閉じる' },
  'layout.railmore.hint': { ko: '창을 키우면 레일로 돌아옵니다', en: 'They return to the rail on a taller window', ja: 'ウィンドウを広げるとレールに戻ります' },
  'layout.railmore.hint_pin': { ko: '전체메뉴에서 별(★)로 고정한 메뉴는 접히지 않습니다', en: 'Menus you star in All menus are never folded', ja: '全メニューで★を付けたメニューは折りたたまれません' },
  'layout.allmenu.pin_full': { ko: '우측 레일에 최대 {n}개까지 고정할 수 있습니다', en: 'You can pin up to {n} menus', ja: '右レールには最大 {n} 件まで固定できます' },

  // ===== 2026-08-11 — 세로 레일(72px) 전용 축약 라벨 =====
  // 좌측 메뉴가 세로 배치(라벨 10px · 확보폭 63px)로 좁아져 일부 라벨이 한 줄에 안 들어간다.
  // **두 줄로 늘리지 않고 축약**한다(사용자 지시) — 항목 높이 47px 균일성을 지키기 위해서다.
  //
  // 실측(scripts/measure-labels 기준, 10px / letter-spacing -0.045em / 확보 63px):
  //   ko 10개 전부 OK (최장 "워크스페이스" 58px)
  //   en 10개 전부 OK (최장 "Organization" 52px) → 축약 불필요, 전체 라벨 유지
  //   ja 2개 넘침 — "ダッシュボード" 67px(+4) · "ワークスペース" 67px(+4) ← 아래 두 항목만 축약
  //
  // ⚠ ja 축약은 **의미 기반**이다(2026-08-11 사용자 지시로 머리 절단 → 의미 기반 변경).
  //   ダッシュボード → 概要   (화면이 보여주는 것 = 요약/개요)
  //   ワークスペース → ファイル (Folder 아이콘 · 파일·문서 공유 화면의 실체)
  //   즉 원어의 잘린 조각이 아니라 그 화면을 가리키는 짧은 일본어다 —
  //   「ダッシュ」「ワークス」 같은 절단형으로 "되돌리지" 말 것. 의도된 선택이다.
  // ko/en 은 전체 라벨과 동일하게 채워 두어 해당 언어에서는 변화가 없다.
  'layout.nav.short.dashboard': { ko: '대시보드', en: 'Dashboard', ja: '概要' },
  'layout.nav.short.workspace': { ko: '워크스페이스', en: 'Workspace', ja: 'ファイル' },

  // ===== 2026-08-11 — ⑥ 상태바 채팅 드롭다운(ChatDropdown) =====
  // 상태바의 "채팅"을 누르면 알림 벨과 같은 방식으로 대화 내역이 위로 열린다.
  'layout.status.aria_chat': { ko: '채팅', en: 'Chats', ja: 'チャット' },
  'layout.status.aria_chat_count': { ko: '채팅 {n}건', en: 'Chats: {n}', ja: 'チャット {n}件' },
  'layout.chatdrop.title': { ko: '대화 내역', en: 'Conversations', ja: '会話履歴' },
  'layout.chatdrop.unread_badge': { ko: '안읽음 {n}', en: '{n} unread', ja: '未読 {n}' },
  'layout.chatdrop.loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中…' },
  'layout.chatdrop.error': { ko: '대화 내역을 불러오지 못했습니다', en: 'Failed to load conversations', ja: '会話履歴を読み込めませんでした' },
  'layout.chatdrop.empty': { ko: '대화가 없습니다', en: 'No conversations', ja: '会話がありません' },
  'layout.chatdrop.view_all': { ko: '전체 보기', en: 'View all', ja: 'すべて表示' },
  'layout.chatdrop.aria_list': { ko: '대화 내역 목록', en: 'Conversation list', ja: '会話履歴リスト' },
  'layout.chatdrop.ai_room': { ko: 'AI 비서', en: 'AI assistant', ja: 'AIアシスタント' },
  'layout.chatdrop.no_name': { ko: '(이름 없음)', en: '(Untitled)', ja: '(名称未設定)' },

  // ===== 2026-08-12 — ⑥ 상태바 근무 상태(StatusDropdown) =====
  // "상태를 클릭하면 출근·퇴근·회의 등의 상태가 표시" — 첫 칸이 장식에서 선택 UI 가 됐다.
  'layout.workstatus.none': { ko: '상태', en: 'Status', ja: 'ステータス' },
  // 레일 머리(H1-4)가 쓰는 「아직 정하지 않음」 — 상태바의 자리표시(`none`, 「상태」)와 다르다.
  'layout.workstatus.unset': { ko: '미설정', en: 'Not set', ja: '未設定' },
  // ① 레일 펼치기·접기 · 출근 전 표기 (정본 H1-4)
  'layout.rail.expand': { ko: '메뉴 펼치기', en: 'Expand menu', ja: 'メニューを開く' },
  'layout.rail.collapse': { ko: '메뉴 접기', en: 'Collapse menu', ja: 'メニューを閉じる' },
  'layout.rail.before_checkin': { ko: '출근 전', en: 'Before check-in', ja: '出勤前' },
  'layout.rail.scroll_up': { ko: '메뉴 위로', en: 'Scroll menu up', ja: 'メニューを上へ' },
  'layout.rail.scroll_down': { ko: '메뉴 아래로', en: 'Scroll menu down', ja: 'メニューを下へ' },
  'layout.workstatus.title': { ko: '근무 상태', en: 'Work status', ja: '勤務ステータス' },
  'layout.workstatus.aria': { ko: '근무 상태 선택', en: 'Select work status', ja: '勤務ステータスを選択' },
  'layout.workstatus.aria_current': { ko: '근무 상태 — {name}', en: 'Work status — {name}', ja: '勤務ステータス — {name}' },
  'layout.workstatus.work': { ko: '출근중', en: 'Working', ja: '出勤中' },
  'layout.workstatus.meeting': { ko: '회의중', en: 'In a meeting', ja: '会議中' },
  'layout.workstatus.away': { ko: '자리비움', en: 'Away', ja: '離席中' },
  'layout.workstatus.out': { ko: '외근', en: 'Out of office', ja: '外勤' },
  // 2026-08-12 프로필 팝오버 '근태 설정' 요구("출근, 퇴근, 출장 등")로 추가.
  'layout.workstatus.trip': { ko: '출장', en: 'Business trip', ja: '出張' },
  'layout.workstatus.off': { ko: '퇴근', en: 'Off duty', ja: '退勤' },
  // 항목 설명 — 어디에 남는지(서버 근태 vs 이 브라우저)를 밝혀 오해를 막는다.
  'layout.workstatus.desc.work': { ko: '근태에 출근 시각을 기록합니다', en: 'Records your check-in time', ja: '出勤時刻を勤怠に記録します' },
  'layout.workstatus.desc.off': { ko: '근태에 퇴근 시각을 기록합니다', en: 'Records your check-out time', ja: '退勤時刻を勤怠に記録します' },
  /**
   * ★★★ **2026-09-06 · 판 C — 이 문장은 거짓이 됐다.** 회의중·자리비움·외근·출장이
   *    서버 이력(`hr_work_status_logs`)에 남으면서 「이 브라우저에만」이 더는 참이 아니다.
   *    화면이 스스로 적어 둔 안내는 기능이 바뀔 때 **함께** 고쳐야 한다.
   */
  'layout.workstatus.desc.local': { ko: '근태 이력에 남습니다', en: 'Recorded in your work log', ja: '勤怠履歴に残ります' },
  'layout.workstatus.saving': { ko: '기록 중…', en: 'Saving…', ja: '記録中…' },
  // ⑥ 상태바 드롭다운 개편(2026-08-20) — 오늘 기록 요약 줄.
  'layout.workstatus.today': { ko: '오늘', en: 'Today', ja: '今日' },
  'layout.workstatus.me': { ko: '내 정보', en: 'My info', ja: '自分の情報' },
  // 두 칸짜리 좁은 버튼이라 desc.* 원문(…시각을 기록합니다)은 두 줄로 접힌다. 뜻은 같고 길이만 줄인 것.
  'layout.workstatus.pick.work': { ko: '출근 시각 기록', en: 'Records check-in', ja: '出勤時刻を記録' },
  'layout.workstatus.pick.off': { ko: '퇴근 시각 기록', en: 'Records check-out', ja: '退勤時刻を記録' },
  // 퇴근 잠금(2026-08-23) — "퇴근은 출근한 사람만"(근태 2차 설계 화면 E). 서버가 400 으로
  // 거절하던 것을 화면이 먼저 말해 준다. 상태바 드롭다운·프로필 팝오버가 같은 문장을 쓴다.
  'layout.workstatus.locked_off': { ko: '출근해야 열립니다', en: 'Available after check-in', ja: '出勤後に利用できます' },

  // ===== 2026-09-06 — 근무 상태내역(판 C) =====
  // 「고른 날」의 오른쪽 칸. 낱말은 상태바·레일과 **같은 것**을 쓴다(`layout.workstatus.*`) —
  // 한 값이 자리마다 다른 이름을 가지면 같은 사람의 하루가 화면마다 달라 보인다.
  'layout.worklog.title': { ko: '근무 상태내역', en: 'Work status log', ja: '勤務ステータス履歴' },
  'layout.worklog.count': { ko: '{n}건', en: '{n} entries', ja: '{n}件' },
  'layout.worklog.empty': { ko: '그날은 남은 기록이 없습니다.', en: 'Nothing was recorded that day.', ja: 'その日の記録はありません。' },
  'layout.worklog.check_in': { ko: '출근', en: 'Check-in', ja: '出勤' },
  'layout.worklog.check_out': { ko: '퇴근', en: 'Check-out', ja: '退勤' },
  'layout.worklog.check_out_cancel': { ko: '퇴근 취소', en: 'Check-out canceled', ja: '退勤取消' },
  // 「누가 남긴 것인가」 — 실측으로 찍은 퇴근과 마감으로 채운 퇴근을 같은 배지로 그리면
  // 나중에 「이 18:30 은 실제로 나간 시각인가」를 물을 수 없다.
  'layout.worklog.src.record': { ko: '서버 기록', en: 'Server record', ja: 'サーバー記録' },
  'layout.worklog.src.self': { ko: '본인', en: 'By you', ja: '本人' },
  'layout.worklog.src.closed': { ko: '소정 종료 마감', en: 'Closed at scheduled end', ja: '所定終業で締め' },
  'layout.worklog.late': { ko: '지각 {n}분', en: '{n} min late', ja: '遅刻{n}分' },
  // 퇴근 취소(질의 ⑧) — 레일에서만 쓴다. 같은 날이 아니면 길이 다르다(근태 정정 결재).
  'layout.workstatus.cancel_off': { ko: '퇴근 취소', en: 'Undo check-out', ja: '退勤を取り消す' },
  'layout.workstatus.cancel_off_ask': { ko: '퇴근 기록을 지울까요?', en: 'Remove your check-out?', ja: '退勤記録を削除しますか？' },
  'layout.workstatus.cancel_off_yes': { ko: '지웁니다', en: 'Remove', ja: '削除' },
  'layout.workstatus.cancel_off_no': { ko: '그만두기', en: 'Keep', ja: 'やめる' },
  'layout.workstatus.cancel_off_failed': { ko: '퇴근 취소에 실패했습니다. 잠시 후 다시 시도해 주세요.', en: 'Could not undo the check-out. Please try again.', ja: '退勤の取り消しに失敗しました。しばらくしてから再度お試しください。' },
  // 상태를 서버에 못 남겼을 때 — 조용히 성공한 척하지 않는다(다음 기기에서 안 보인다).
  'layout.workstatus.status_local_only': { ko: '상태를 서버에 남기지 못했습니다 — 이 기기에만 보입니다.', en: 'Could not save your status — it is shown on this device only.', ja: 'ステータスを保存できませんでした — この端末にのみ表示されます。' },

  // ===== 2026-08-12 — ⑤ 레일 프로필 팝오버(ProfilePopover) =====
  // 아바타를 누르면 신상(사진·이름·직책·이메일) + 근태 설정 · 설정 · 로그아웃이 한 자리에 모인다.
  'layout.profile.aria': { ko: '내 프로필 메뉴', en: 'My profile menu', ja: 'マイプロフィールメニュー' },
  'layout.profile.attendance': { ko: '근태 설정', en: 'Work status', ja: '勤怠設定' },
  'layout.profile.settings': { ko: '설정', en: 'Settings', ja: '設定' },
  // 내 서명 (2026-08-29 · 판 8 잔여 ②) — 결재 시트 말고도 여기서 만들 수 있다.
  'layout.profile.signature': { ko: '내 서명', en: 'My signature', ja: '私の署名' },
  'layout.profile.logout': { ko: '로그아웃', en: 'Sign out', ja: 'ログアウト' },
  'layout.profile.logout_confirm': {
    ko: '로그아웃하시겠습니까?',
    en: 'Sign out of your account?',
    ja: 'ログアウトしますか？',
  },
  'layout.profile.detail': { ko: '내 프로필 열기', en: 'Open my profile', ja: 'マイプロフィールを開く' },
  'layout.profile.back': { ko: '뒤로', en: 'Back', ja: '戻る' },
  'layout.profile.no_position': { ko: '직책 미등록', en: 'No position set', ja: '役職未設定' },

  // ===== 2026-08-28 — 프로필 팝오버 «잠금» 한 줄 (자리 재정의 · 판 1) =====
  // 화면잠금 자체는 2026-08-19 부터 동작한다(가림막 · 잠금화면 디자인 · 대기시간). 빠진 것은
  // **길**뿐이었다 — 진입점이 ① 유휴 만료 ② 단축키 ③ 설정 모달 안 버튼 셋이라 "그 기능이
  // 있다는 것을 아는 사람만" 썼다. 로그아웃 바로 위에 한 줄을 놓고 키캡을 함께 적는다.
  // 「내 것」으로 가는 길 (2026-08-31 · 시안 맞춤 9/9) — 팝오버가 세 줄뿐이라 길이 없었다.
  'layout.profile.my_hr': { ko: '내 인사 정보', en: 'My HR record', ja: '私の人事情報' },
  'layout.profile.my_attendance': { ko: '내 근태', en: 'My attendance', ja: '私の勤怠' },
  'layout.profile.my_leave': { ko: '내 휴가 · 연차', en: 'My leave', ja: '私の休暇' },
  'layout.profile.my_approval': { ko: '내 결재', en: 'My approvals', ja: '私の決裁' },
  'layout.profile.days': { ko: '{n}일', en: '{n} d', ja: '{n}日' },
  'layout.profile.employee_no': { ko: '사번 {no}', en: 'ID {no}', ja: '社員番号 {no}' },
  'layout.profile.lock': { ko: '잠금', en: 'Lock', ja: 'ロック' },
  // 꺼져 있을 때 — 줄을 **감추지 않는다**. 눌러도 아무 일이 없는 줄을 남기지 않으려고
  // 문구를 바꿔 설정(보안설정 › 잠금화면)으로 보낸다.
  'layout.profile.lock_off': {
    ko: '잠금화면을 먼저 켜세요',
    en: 'Turn on the lock screen first',
    ja: 'ロック画面を先に有効にしてください',
  },
};

/**
 * 세로 레일 전용 라벨 — 축약본이 등록된 키는 축약본, 없으면 전체 라벨.
 *
 * 매핑을 명시 테이블로 두는 이유: 키 문자열을 가공해 추론하면(`layout.nav.x` → `layout.nav.short.x`)
 * 오탈자가 조용히 전체 라벨로 흘러 축약이 적용되지 않는다. 여기 있는 항목만 축약된다 — grep 가능.
 *
 * ⚠ 관리자 메뉴 레지스트리(/menu-config)에서 온 라벨에는 적용되지 않는다. 그건 관리자가 직접
 *   입력한 문자열이라 임의로 줄이면 설정과 화면이 어긋난다 → 넘치면 CSS 가 말줄임(…)으로 처리한다.
 */
const RAIL_SHORT_LABEL: Record<string, string> = {
  'layout.nav.dashboard': 'layout.nav.short.dashboard',
  'layout.nav.workspace': 'layout.nav.short.workspace',
};

export function tLayoutRail(key: LayoutI18nKey): string {
  const short = RAIL_SHORT_LABEL[key as string];
  return tLayout((short ?? key) as LayoutI18nKey);
}

export type LayoutI18nKey = keyof typeof MESSAGES;

/**
 * Cycle 70 — Layout (NavBar/TopBar) i18n lookup.
 * params 치환 — cycle 51-69 의 9 도메인 모듈과 일관.
 */
export function tLayout(key: LayoutI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

// base-i18n thin wrapper (cycle 41 통합 패턴 일관)
export function getLayoutLang(): Lang { return getSharedLang(); }
export function setLayoutLang(value: Lang): void { setSharedLang(value); }
export function subscribeLayoutLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
